import type { WorkflowEvent } from '@/shared/hooks/api/workflows'
import type { LatestWorkflowForEntityResponse } from '@/shared/hooks/api/workflows'

export type ProductInitStepKey =
  | 'ingestion'
  | 'classify_document'
  | 'extract_product'
  | 'persist_result'

const PIPELINE_AFTER_INGESTION: Exclude<ProductInitStepKey, 'ingestion'>[] = [
  'classify_document',
  'extract_product',
]

function eventStep(event: WorkflowEvent): string | null {
  const isInEvent = 'step' in event
  if (!isInEvent || typeof event.step !== 'string') {
    return null
  }
  return event.step
}

export function parseProductInitWorkflowSignals(events: WorkflowEvent[]): {
  completedSteps: Set<string>
  lastActiveStep: string | null
} {
  const completedSteps = new Set<string>()
  let lastActiveStep: string | null = null

  for (const event of events) {
    const step = eventStep(event)
    if (
      event.type === 'step.started' ||
      event.type === 'llm.started' ||
      event.type === 'llm.completed'
    ) {
      if (step) {
        lastActiveStep = step
      }
    }
    if (event.type === 'step.completed' && step) {
      completedSteps.add(step)
    }
  }

  return { completedSteps, lastActiveStep }
}

function skippedReason(
  result: unknown
): 'waiting_for_initial_batch' | 'already_initialized' | null {
  if (result === null || typeof result !== 'object') {
    return null
  }
  const record = result as Record<string, unknown>
  if (record.skipped !== true) {
    return null
  }
  const reason = record.reason
  if (reason === 'waiting_for_initial_batch') {
    return 'waiting_for_initial_batch'
  }
  if (reason === 'already_initialized') {
    return 'already_initialized'
  }
  return null
}

function effectiveLastStepForDisplay(
  lastActiveStep: string | null,
  pipeline: ProductInitStepKey[]
): ProductInitStepKey | null {
  if (lastActiveStep == null) {
    return null
  }
  if (lastActiveStep === 'persist_result') {
    return null
  }
  if (lastActiveStep === 'get_completed_sources') {
    return 'extract_product'
  }
  if (pipeline.includes(lastActiveStep as ProductInitStepKey)) {
    return lastActiveStep as ProductInitStepKey
  }
  if (lastActiveStep === 'extract_product') {
    return 'extract_product'
  }
  return null
}

function pipelineForEvents(_events: WorkflowEvent[]): ProductInitStepKey[] {
  return ['ingestion', ...PIPELINE_AFTER_INGESTION]
}

export type ProductInitLoaderHeadlineKey = 'waitingBatch' | 'ingestion' | 'workflow'

export function getProductInitLoaderState(input: {
  workflow: LatestWorkflowForEntityResponse | undefined
  isIngesting: boolean
}): {
  headlineKey: ProductInitLoaderHeadlineKey
  latestStepKey: ProductInitStepKey | null
} {
  const workflow = input.workflow
  const events = workflow?.events ?? []
  const status = workflow?.status ?? 'none'
  const result = workflow?.result

  if (
    status === 'completed' &&
    skippedReason(result) === 'waiting_for_initial_batch'
  ) {
    return { headlineKey: 'waitingBatch', latestStepKey: null }
  }

  if (status === 'completed' && skippedReason(result) === 'already_initialized') {
    return { headlineKey: 'workflow', latestStepKey: null }
  }

  const { lastActiveStep } = parseProductInitWorkflowSignals(events)
  const pipeline = pipelineForEvents(events)
  const displayLast = effectiveLastStepForDisplay(lastActiveStep, pipeline)

  if (displayLast) {
    return { headlineKey: 'workflow', latestStepKey: displayLast }
  }

  if (input.isIngesting) {
    return { headlineKey: 'ingestion', latestStepKey: 'ingestion' }
  }

  return { headlineKey: 'workflow', latestStepKey: null }
}
